<template>
  <div class="card mb-25 border-0 rounded-0 bg-white letter-spacing">
    <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
      <div class="d-sm-flex align-items-center">
        <router-link
          :to="{ name: 'AddTariffZonePage' }"
          class="default-btn position-relative transition border-0 fw-medium text-white pt-11 pb-11 ps-25 pe-25 pt-md-12 pb-md-12 ps-md-30 pe-md-30 rounded-1 bg-success fs-md-15 fs-lg-16 d-inline-block me-10 mb-10 mb-lg-0 text-decoration-none"
        >
          <i class="flaticon-plus position-relative ms-5 fs-12"></i>
          Ajouter une zone tarifaire
        </router-link>
      </div>

      <div class="d-flex align-items-center">
        <form class="search-box position-relative me-15" @submit.prevent="rechercher">
          <input
            type="text"
            v-model="searchTerm"
            @keyup="rechercher"
            class="form-control shadow-none text-black rounded-0 border-0"
            placeholder="Rechercher une zone"
          />
          <button type="submit" class="bg-transparent text-primary transition p-0 border-0">
            <i class="flaticon-search-interface-symbol"></i>
          </button>
        </form>
      </div>
    </div>

    <!-- BODY : Tableau -->
    <div class="card-body p-15 p-sm-20 p-md-25">
      <div class="table-responsive">
        <table class="table text-nowrap align-middle mb-0">
          <thead>
            <tr>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Code</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Nom</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Pays</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Port</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Par défaut</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0">Statut</th>
              <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-0 text-end pe-0">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="zone in tariffZones" :key="zone.id">
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">{{ zone.code }}</td>
              <td class="shadow-none lh-1 fw-medium text-black-emphasis">{{ zone.name }}</td>
              <td class="shadow-none lh-1">{{ zone.pays?.name_fr || '-' }}</td>
              <td class="shadow-none lh-1">{{ zone.port?.nomPort || '-' }}</td>
              <td>
                <span class="badge" :class="zone.isDefault ? 'bg-info' : 'bg-secondary'">
                  {{ zone.isDefault ? 'Oui' : 'Non' }}
                </span>
              </td>
              <td>
                <span class="badge" :class="zone.isActive ? 'bg-success' : 'bg-danger'">
                  {{ zone.isActive ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="text-end pe-0">
                <div class="dropdown">
                  <button class="badge bg-primary text-white dropdown-toggle border-0 fs-15" data-bs-toggle="dropdown">
                    Actions <i class="flaticon-chevron-2 ms-1"></i>
                  </button>
                  <ul class="dropdown-menu">
                    <li>
                      <router-link
                        class="dropdown-item"
                        :to="{ name: 'ViewTariffZonePage', params: { id: zone.id } }"
                      >
                        <i class="flaticon-eye me-2"></i> Voir le détail
                      </router-link>
                    </li>

                    <li>
                      <router-link
                        class="dropdown-item"
                        :to="{ name: 'EditTariffZonePage', params: { id: zone.id } }"
                      >
                        <i class="flaticon-pen me-2"></i> Modifier
                      </router-link>
                    </li>
                    <li><hr class="dropdown-divider"></li>
                    <li>
                      <a
                        class="dropdown-item text-danger"
                        href="#"
                        @click.prevent="suppression(zone.id, tariffZones, 'tariff-zone', 'zone tarifaire')"
                      >
                        <i class="flaticon-delete me-2"></i> Supprimer
                      </a>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-area d-md-flex mt-15 mt-sm-20 mt-md-25 justify-content-between align-items-center">
        <PaginationComponent
          :page="page"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import ApiService from "../../services/ApiService";
import { suppression, error } from "../../utils/utils";
import PaginationComponent from "../../components/Utilities/Pagination.vue";

export default defineComponent({
  name: "ListeTariffZone",
  components: {
    PaginationComponent

  },
  setup() {
    const tariffZones = ref<any[]>([]);
    const searchTerm = ref("");
    const page = ref(1);
    const totalPages = ref(0);
    const limit = ref(10);
    const totalElements = ref(0);

    const getAllTariffZones = (p = 1, l = 10, search = "") => {
      ApiService.get(`/tariff-zones?page=${p}&limit=${l}&mot=${search}&`)
        .then(({ data }) => {
          tariffZones.value = data.data.data;
          totalPages.value = data.data.totalPages;
          limit.value = data.data.limit;
          totalElements.value = data.data.totalElements;
        })
        .catch(({ response }) => {
          error(response?.data?.message || "Erreur de chargement");
        });
    };

    const handlePaginate = ({ page_, limit_ }: { page_: number; limit_: number }) => {
      page.value = page_;
      getAllTariffZones(page_, limit_, searchTerm.value);
    };

    const rechercher = () => {
      page.value = 1;
      getAllTariffZones(1, limit.value, searchTerm.value);
    };

    onMounted(() => {
      getAllTariffZones();
    });

    return {
      tariffZones,
      searchTerm,
      page,
      totalPages,
      totalElements,
      limit,
      rechercher,
      handlePaginate,
      suppression,
    };
  },
});
</script>